import { CHRONIC_REASON_OPTIONS, isChronicBillingEventBilled, parseChronicDescription } from "../../../../utils/chronicBilling";
import { formatDate, formatDateTime } from "../controleFormat";
import { CHARTER_THEME } from "../controleTheme";
import { historyFieldLabel, historyItemCommentWithSource } from "../patientHistory";
import {
    controleChronicColumnStyle,
    controleChronicDeleteButtonStyle,
    controleChronicFieldStyle,
    controleChronicGridStyle,
    controleChronicHeaderStyle,
    controleChronicHistoryButtonStyle,
    controleChronicMarkBilledButtonStyle,
    controleChronicMetaStyle,
    controleChronicPanelStyle,
    controleChronicReasonFieldStyle,
    controleChronicRowActionsStyle,
    controleChronicRowStyle,
    controleChronicRowsStyle,
    controleChronicSubtitleStyle,
    controleChronicTitleStyle,
} from "../styles/chronicStyles";
import {
    controlePatientPdaEditorStyle,
    controlePatientPdaEmptyStyle,
    controlePatientPdaHistoryItemStyle,
    controlePatientPdaHistoryStyle,
    controlePatientPdaLabelStyle,
    controlePatientPdaPanelStyle,
    controlePdaSaveRemarkButtonStyle,
} from "../styles/patientStyles";

// Panneaux de la fiche patient communs aux écrans Contrôle et Contrôle Bis.
// disabled fige le panneau (pré-production validée dans Contrôle Bis).

const pdaTextareaStyle = inputStyle => ({ ...inputStyle, width: "100%", minHeight: "96px", resize: "vertical", lineHeight: 1.4 });
const chronicCommentTextareaStyle = inputStyle => ({ ...inputStyle, minHeight: "58px", resize: "vertical", lineHeight: 1.35, width: "100%" });

export function PdaRemarkPanel({ controle, editing, patient, disabled = false, idPrefix }) {
    const { inputStyle } = CHARTER_THEME;
    const pdaRemarkEdited = String(controle.pdaRemarkDraft || "") !== String(patient.pdaProductionRemark || "");
    return (
        <section style={controlePatientPdaPanelStyle}>
            <div style={controlePatientPdaEditorStyle}>
                <label style={controlePatientPdaLabelStyle} htmlFor={`${idPrefix}-pda-remark-${patient.patientId}`}>
                    Remarque production PDA
                </label>
                <textarea
                    id={`${idPrefix}-pda-remark-${patient.patientId}`}
                    value={controle.pdaRemarkDraft}
                    onChange={event => controle.setPdaRemarkDraft(event.target.value)}
                    placeholder="Aucune remarque PDA renseignée."
                    disabled={editing.pdaRemarkSaving || disabled}
                    style={pdaTextareaStyle(inputStyle)}
                />
                {pdaRemarkEdited && (
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    <button
                        type="button"
                        onClick={() => editing.savePdaRemark(patient)}
                        disabled={editing.pdaRemarkSaving || disabled}
                        style={{
                            ...controlePdaSaveRemarkButtonStyle,
                            opacity: editing.pdaRemarkSaving || disabled ? 0.55 : 1,
                            cursor: editing.pdaRemarkSaving || disabled ? "not-allowed" : "pointer",
                        }}
                    >
                        Enregistrer la remarque
                    </button>
                    </div>
                )}
            </div>
            <div style={controlePatientPdaHistoryStyle}>
                <div style={controlePatientPdaLabelStyle}>Historique des remarques</div>
                {controle.pdaHistoryLoading ? (
                    <div style={controlePatientPdaEmptyStyle}>Chargement de l'historique...</div>
                ) : controle.pdaHistory.length === 0 ? (
                    <div style={controlePatientPdaEmptyStyle}>Aucun commentaire robot ou remarque PDA pour ce patient.</div>
                ) : (
                    <div style={{ display: "grid", gap: "8px" }}>
                        {controle.pdaHistory.map(item => (
                            <div key={item.id} style={controlePatientPdaHistoryItemStyle}>
                                <strong style={{ color: "#0f172a", fontSize: "13px", overflowWrap: "anywhere" }}>
                                    {historyItemCommentWithSource(item)}
                                </strong>
                                <span style={{ color: "#64748b", fontSize: "12px", overflowWrap: "anywhere" }}>
                                    {historyFieldLabel(item.fieldName)}
                                    {item.oldValue ? ` · ${item.oldValue}` : ""}
                                    {" · "}
                                    {item.changedAt ? new Date(item.changedAt).toLocaleString("fr-FR") : "Date inconnue"}
                                    {item.changedBy ? ` · ${item.changedBy}` : ""}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

function ChronicBillingLine({ chronic, patient, event, disabled, inputStyle }) {
    const info = parseChronicDescription(event.description);
    const locked = chronic.savingId === event.id || disabled;
    const lockedButtonStyle = { opacity: locked ? 0.55 : 1, cursor: locked ? "not-allowed" : "pointer" };
    return (
        <div style={controleChronicRowStyle}>
            <div style={{ minWidth: 0 }}>
                <strong style={{ color: "#1e293b", fontSize: "13px", overflowWrap: "anywhere" }}>
                    {info.productInfo || "Produit sans information"}
                </strong>
                <small style={controleChronicMetaStyle}>
                    Date produit : {formatDate(event.date)}
                    {" · "}
                    Ajouté le {formatDateTime(event.createdAt) || "date inconnue"}
                    {event.createdBy ? ` par ${event.createdBy}` : ""}
                </small>
            </div>
            <label style={controleChronicReasonFieldStyle}>
                <span>Motif</span>
                <select
                    value={info.reason}
                    onChange={eventSelect => chronic.updateReason(patient, event, eventSelect.target.value)}
                    disabled={locked}
                    style={inputStyle}
                >
                    <option value="">—</option>
                    {CHRONIC_REASON_OPTIONS.filter(Boolean).map(reason => (
                        <option key={reason} value={reason}>{reason}</option>
                    ))}
                </select>
            </label>
            <label style={controleChronicReasonFieldStyle}>
                <span>Commentaire</span>
                <textarea
                    defaultValue={info.comment}
                    onBlur={commentEvent => chronic.updateComment(patient, event, commentEvent.target.value)}
                    disabled={locked}
                    placeholder="Commentaire produit"
                    style={chronicCommentTextareaStyle(inputStyle)}
                />
            </label>
            <div style={controleChronicRowActionsStyle}>
                <button
                    type="button"
                    onClick={() => chronic.setBillCandidate({ patient, event })}
                    disabled={locked}
                    style={{ ...controleChronicMarkBilledButtonStyle, ...lockedButtonStyle }}
                >
                    Marquer facturé
                </button>
                <button
                    type="button"
                    onClick={() => chronic.setDeleteCandidate({ patient, event })}
                    disabled={locked}
                    style={{ ...controleChronicDeleteButtonStyle, ...lockedButtonStyle }}
                >
                    Retirer de la facturation
                </button>
            </div>
        </div>
    );
}

export function ChronicBillingPanel({ chronic, editing, patient, disabled = false }) {
    const { inputStyle, actionButtonStyle } = CHARTER_THEME;
    const chronicEvents = (patient.billingEvents || [])
        .filter(event => event.type === "CHRONIC")
        .filter(event => !isChronicBillingEventBilled(event));
    return (
        <section style={controleChronicPanelStyle}>
            <div style={controleChronicHeaderStyle}>
                <div>
                    <h3 style={controleChronicTitleStyle}>Facturation chronique</h3>
                    <p style={controleChronicSubtitleStyle}>
                        {chronicEvents.length} produit{chronicEvents.length > 1 ? "s" : ""} à facturer
                    </p>
                </div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    <button
                        type="button"
                        onClick={() => chronic.setHistoryPatient(patient)}
                        style={controleChronicHistoryButtonStyle}
                    >
                        Voir historique
                    </button>
                    <button
                        type="button"
                        onClick={() => chronic.openModal(patient)}
                        disabled={disabled}
                        style={{
                            ...actionButtonStyle,
                            opacity: disabled ? 0.55 : 1,
                            cursor: disabled ? "not-allowed" : "pointer",
                        }}
                    >
                        Ajouter un produit
                    </button>
                </div>
            </div>
            <label style={{ ...controleChronicFieldStyle, marginBottom: "10px" }}>
                <span>Date de dernière facturation</span>
                <input
                    type="date"
                    value={patient.chronicLastBillingDate || ""}
                    onChange={event => editing.savePatch(
                        patient,
                        { chronicLastBillingDate: event.target.value || null },
                        "Impossible d'enregistrer la date de dernière facturation."
                    )}
                    disabled={disabled}
                    style={inputStyle}
                />
            </label>
            <div style={controleChronicGridStyle}>
                <div style={controleChronicColumnStyle}>
                    {chronicEvents.length === 0 ? (
                        <div style={controlePatientPdaEmptyStyle}>Aucune facturation chronique pour ce patient.</div>
                    ) : (
                        <div style={controleChronicRowsStyle}>
                            {chronicEvents.map(event => (
                                <ChronicBillingLine key={event.id} chronic={chronic} patient={patient} event={event} disabled={disabled} inputStyle={inputStyle} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
